import { useState, useEffect, type FormEvent } from "react";
import { useListInvestors, useCreateInvestor, getListInvestorsQueryKey, useListInvestorAccess, getListInvestorAccessQueryKey, useListCompanies, getListCompaniesQueryKey, useListCohorts } from "@workspace/api-client-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, UserCircle, Shield, X, Key, Phone, IndianRupee, Bell, FileText, ExternalLink, Trash2, Loader2, Check, Copy, Mail, MailCheck, FileSpreadsheet } from "lucide-react";
import InfoTip from "@/components/info-tip";
import { useToast } from "@/hooks/use-toast";
import { useCohort } from "@/hooks/use-cohort";
import { loadInvestorMemberships, saveInvestorMemberships } from "./investor-cohort-memberships";

const BASE = import.meta.env.BASE_URL;

// All `/api/investor-access*` mutations require the admin token (added when
// POST/DELETE were locked behind requireAdmin). Without this, every Save
// Access click silently 401s. The token is set on admin login and stored in
// localStorage; same convention as admin-investors.tsx and cohort-context.tsx.
function authHeaders(): Record<string, string> {
  const token = localStorage.getItem("admin_token") || "";
  return { "Content-Type": "application/json", "x-admin-token": token };
}

function fmtINRShort(n: number | null | undefined): string {
  if (!n || n <= 0) return "";
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(0)}K`;
  return `₹${n}`;
}

const typeBadgeStyles: Record<string, string> = {
  angel: "bg-purple-100 text-purple-800",
  vc: "bg-blue-100 text-blue-800",
  family_office: "bg-emerald-100 text-emerald-800",
  syndicate: "bg-amber-100 text-amber-800",
};

export default function Investors({ cohortSlug }: { cohortSlug?: string } = {}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [accessDialogOpen, setAccessDialogOpen] = useState(false);
  const [selectedInvestorId, setSelectedInvestorId] = useState<number | null>(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordInvestorId, setPasswordInvestorId] = useState<number | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [settingPassword, setSettingPassword] = useState(false);
  const [rofrDialogOpen, setRofrDialogOpen] = useState(false);
  const [rofrs, setRofrs] = useState<any[]>([]);
  const [docDialogOpen, setDocDialogOpen] = useState(false);
  const [docInvestorId, setDocInvestorId] = useState<number | null>(null);
  const [investorDocs, setInvestorDocs] = useState<any[]>([]);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { cohortId, isLoading: cohortLoading } = useCohort(cohortSlug);
  const cohortList = useListCohorts();
  const { data: investors, isLoading } = useListInvestors();
  const companyQuery = useListCompanies({ cohortId }, { query: { enabled: !cohortSlug || !!cohortId, queryKey: getListCompaniesQueryKey({ cohortId }) } });
  const companies = companyQuery.data;
  const accessQuery = useListInvestorAccess();
  const allAccess = accessQuery.data;
  const membershipQuery = useQuery({
    queryKey: ["manage-investor-cohorts", selectedInvestorId],
    enabled: accessDialogOpen && selectedInvestorId !== null,
    queryFn: () => loadInvestorMemberships(BASE, selectedInvestorId!, authHeaders()),
    retry: false,
    staleTime: 0,
  });
  const createMutation = useCreateInvestor();

  const companyMap = new Map((companies ?? []).map(c => [c.id, c.name]));

  // Per-row "Send onboarding email" state. Keyed by investor id so multiple rows
  // can be in flight independently. Only one in-flight per row at a time.
  const [onboardingInFlight, setOnboardingInFlight] = useState<Record<number, boolean>>({});

  // Human-friendly "sent X ago" label. Coarse on purpose — admin doesn't need
  // seconds-precision; they need to know "did this go out today or last month".
  const formatRelative = (iso: string | null | undefined): string => {
    if (!iso) return "";
    const then = new Date(iso).getTime();
    if (!Number.isFinite(then)) return "";
    const diffMs = Date.now() - then;
    if (diffMs < 60_000) return "just now";
    const mins = Math.floor(diffMs / 60_000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    return `${months}mo ago`;
  };

  const handleSendOnboarding = async (investor: any) => {
    if (onboardingInFlight[investor.id]) return;
    const alreadySent = !!investor.onboardingEmailSentAt;
    if (alreadySent) {
      const ok = window.confirm(
        `Resend the password-setup email to ${investor.email}?\n\nLast sent ${formatRelative(investor.onboardingEmailSentAt)}. The previous link will be invalidated.`,
      );
      if (!ok) return;
    }
    setOnboardingInFlight(prev => ({ ...prev, [investor.id]: true }));
    try {
      const res = await fetch(`${BASE}api/investors/${investor.id}/send-onboarding-email`, {
        method: "POST",
        headers: authHeaders(),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Send failed (${res.status})`);
      }
      await queryClient.invalidateQueries({ queryKey: getListInvestorsQueryKey() });
      toast({
        title: alreadySent ? "Onboarding email re-sent" : "Onboarding email sent",
        description: `${investor.name} (${investor.email}) will get the password-setup link shortly.`,
      });
    } catch (err: any) {
      toast({
        title: "Could not send onboarding email",
        description: err?.message || "Unknown error",
        variant: "destructive",
      });
    } finally {
      setOnboardingInFlight(prev => {
        const next = { ...prev };
        delete next[investor.id];
        return next;
      });
    }
  };

  const handleCreate = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    createMutation.mutate(
      {
        data: {
          name: form.get("name") as string,
          email: form.get("email") as string,
          type: form.get("type") as "angel" | "vc" | "family_office" | "syndicate",
          firm: (form.get("firm") as string) || null,
          notes: (form.get("notes") as string) || null,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListInvestorsQueryKey() });
          setDialogOpen(false);
          toast({ title: "Investor created" });
        },
      }
    );
  };

  const [accessSelections, setAccessSelections] = useState<Record<number, "summary" | "full" | null>>({});
  const [amountSelections, setAmountSelections] = useState<Record<number, number>>({});
  const [bulkTotalStr, setBulkTotalStr] = useState("");
  const [grantingBatch, setGrantingBatch] = useState(false);
  const [selectedCohorts, setSelectedCohorts] = useState<number[]>([]);
  const [savingCohorts, setSavingCohorts] = useState(false);
  const [cohortDraftReady, setCohortDraftReady] = useState(false);

  useEffect(() => {
    if (accessDialogOpen && membershipQuery.isSuccess && !membershipQuery.isFetching && !cohortDraftReady) {
      setSelectedCohorts(membershipQuery.data.filter(c => c.membershipStatus === "active").map(c => c.id));
      setCohortDraftReady(true);
    }
  }, [accessDialogOpen, membershipQuery.isSuccess, membershipQuery.isFetching, membershipQuery.data, cohortDraftReady]);

  const handleSaveCohorts = async () => {
    if (!selectedInvestorId || !membershipQuery.isSuccess || membershipQuery.isFetching || !cohortDraftReady || savingCohorts) return;
    setSavingCohorts(true);
    try {
      const result = await saveInvestorMemberships(BASE, selectedInvestorId, membershipQuery.data.filter(c => c.membershipStatus === "active").map(c => c.id), selectedCohorts, authHeaders());
      // Always reload from the server, including after partial failure. Do not
      // treat a partially persisted draft as the new baseline for future saves.
      const refreshed = await membershipQuery.refetch();
      if (refreshed.isSuccess) setSelectedCohorts(refreshed.data.filter(c => c.membershipStatus === "active").map(c => c.id));
      else {
        setCohortDraftReady(false);
        throw new Error("Memberships changed, but reloading them failed. Retry loading before editing again.");
      }
      if (result.failures.length) {
        toast({ title: `Saved ${result.saved}; ${result.failures.length} failed`, description: result.failures.join("; "), variant: "destructive" });
      } else {
        toast({ title: "Cohort memberships saved", description: `${result.saved} change${result.saved === 1 ? "" : "s"} saved.` });
      }
    } catch (error) {
      toast({ title: "Could not save cohort memberships", description: error instanceof Error ? error.message : "Network error", variant: "destructive" });
    } finally {
      setSavingCohorts(false);
    }
  };

  useEffect(() => {
    if (accessDialogOpen && selectedInvestorId && companies && allAccess) {
      const existing = (allAccess ?? []).filter(a => a.investorId === selectedInvestorId);
      const existingMap = new Map(existing.map(a => [a.companyId, a as any]));
      const selections: Record<number, "summary" | "full" | null> = {};
      const amounts: Record<number, number> = {};
      companies.forEach(c => {
        const e: any = existingMap.get(c.id);
        selections[c.id] = (e?.accessLevel as "summary" | "full" | undefined) ?? null;
        if (e?.amountInvested) amounts[c.id] = Number(e.amountInvested);
      });
      setAccessSelections(selections);
      setAmountSelections(amounts);
      setBulkTotalStr("");
    }
  }, [accessDialogOpen, selectedInvestorId, companies, allAccess]);

  const toggleCompanyAccess = (companyId: number) => {
    setAccessSelections(prev => ({
      ...prev,
      [companyId]: prev[companyId] ? null : "full",
    }));
  };

  const cycleAccessLevel = (companyId: number) => {
    setAccessSelections(prev => ({
      ...prev,
      [companyId]: prev[companyId] === "summary" ? "full" : "summary",
    }));
  };

  const grantAll = (level: "summary" | "full") => {
    const newSelections: Record<number, "summary" | "full" | null> = {};
    companies?.forEach(c => { newSelections[c.id] = level; });
    setAccessSelections(newSelections);
  };

  const clearAll = () => {
    const newSelections: Record<number, "summary" | "full" | null> = {};
    companies?.forEach(c => { newSelections[c.id] = null; });
    setAccessSelections(newSelections);
    setAmountSelections({});
  };

  const selectedCompanyIds = Object.entries(accessSelections).filter(([, v]) => v).map(([k]) => parseInt(k, 10));
  const selectedCount = selectedCompanyIds.length;

  // Distribute the typed total equally across all currently-selected companies.
  // The remainder (from integer division) is added to the first selected row so
  // the totals always reconcile to the input. Unselected rows are left alone.
  const distributeEqually = () => {
    const total = Number(bulkTotalStr);
    if (!(total > 0) || selectedCount === 0) return;
    const per = Math.floor(total / selectedCount);
    const remainder = total - (per * selectedCount);
    const next: Record<number, number> = { ...amountSelections };
    selectedCompanyIds.forEach((id, i) => { next[id] = per + (i === 0 ? remainder : 0); });
    setAmountSelections(next);
  };

  // Save flow:
  //  - Newly selected (level set, no existing row) → POST then PATCH amount.
  //  - Deselected (level cleared, existing row present) → DELETE.
  //  - Existing row with changed level OR changed amount → PATCH (preserves
  //    the amountInvested/entryDate that were already on the row, unlike the
  //    old DELETE-then-POST flow which would silently lose them).
  // All requests carry the admin token via authHeaders().
  const handleSaveAccess = async () => {
    if (!selectedInvestorId || !companyQuery.isSuccess || !accessQuery.isSuccess || (cohortSlug && !cohortId) || companies?.length === 0) return;
    setGrantingBatch(true);

    const existingAccess = (allAccess ?? []).filter(a => a.investorId === selectedInvestorId);
    const existingMap = new Map<number, any>(existingAccess.map(a => [a.companyId, a as any]));
    let okCount = 0; let failCount = 0;

    try {
      for (const [companyIdStr, level] of Object.entries(accessSelections)) {
        const companyId = parseInt(companyIdStr, 10);
        const existing: any = existingMap.get(companyId);
        const plannedAmount = amountSelections[companyId];
        const hasPlannedAmount = typeof plannedAmount === "number" && plannedAmount > 0;

        if (level && !existing) {
          const r = await fetch(`${BASE}api/investor-access`, {
            method: "POST", headers: authHeaders(),
            body: JSON.stringify({ investorId: selectedInvestorId, companyId, accessLevel: level }),
          });
          if (r.ok) {
            okCount++;
            if (hasPlannedAmount) {
              const created: any = await r.json();
              // Count the PATCH separately — silent failure here would leave a row
              // with access but no amount, while UI claims everything saved.
              const p = await fetch(`${BASE}api/investor-access/${created.id}`, {
                method: "PATCH", headers: authHeaders(),
                body: JSON.stringify({ amountInvested: plannedAmount, accessLevel: level }),
              });
              if (p.ok) okCount++; else failCount++;
            }
          } else failCount++;
        } else if (!level && existing) {
          const r = await fetch(`${BASE}api/investor-access/${existing.id}`, { method: "DELETE", headers: authHeaders() });
          if (r.ok || r.status === 204) okCount++; else failCount++;
        } else if (level && existing) {
          const updates: any = {};
          if (level !== existing.accessLevel) updates.accessLevel = level;
          if (hasPlannedAmount && plannedAmount !== Number(existing.amountInvested ?? 0)) {
            updates.amountInvested = plannedAmount;
          }
          if (Object.keys(updates).length > 0) {
            const r = await fetch(`${BASE}api/investor-access/${existing.id}`, {
              method: "PATCH", headers: authHeaders(),
              body: JSON.stringify(updates),
            });
            if (r.ok) okCount++; else failCount++;
          }
        }
      }
      await queryClient.invalidateQueries({ queryKey: getListInvestorAccessQueryKey() });
      if (failCount === 0) {
        setAccessDialogOpen(false);
        setBulkTotalStr("");
        toast({ title: "Access updated", description: okCount > 0 ? `${okCount} change${okCount === 1 ? "" : "s"} saved.` : undefined });
      } else {
        toast({ title: `Saved ${okCount}, failed ${failCount}`, description: "Check console for details and retry.", variant: "destructive" });
      }
    } catch (e: any) {
      toast({ title: "Failed to update access", description: e?.message || "Network error", variant: "destructive" });
    } finally {
      setGrantingBatch(false);
    }
  };

  // Use raw fetch (not the api-client mutation) so the admin token is sent —
  // the generated client doesn't know about our X-Admin-Token convention.
  const handleRevoke = async (accessId: number) => {
    try {
      const r = await fetch(`${BASE}api/investor-access/${accessId}`, { method: "DELETE", headers: authHeaders() });
      if (r.ok || r.status === 204) {
        queryClient.invalidateQueries({ queryKey: getListInvestorAccessQueryKey() });
        toast({ title: "Access revoked" });
      } else {
        toast({ title: "Revoke failed", description: `HTTP ${r.status}`, variant: "destructive" });
      }
    } catch (e: any) {
      toast({ title: "Revoke failed", description: e?.message || "Network error", variant: "destructive" });
    }
  };

  const handleSetPassword = async () => {
    if (!passwordInvestorId || !newPassword || newPassword.length < 8) return;
    setSettingPassword(true);
    try {
      const res = await fetch(`${BASE}api/investors/${passwordInvestorId}/set-password`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ password: newPassword }),
      });
      if (res.ok) {
        toast({ title: "Portal password set successfully" });
        setPasswordDialogOpen(false);
        setNewPassword("");
      } else {
        const err = await res.json();
        toast({ title: "Error", description: err.error, variant: "destructive" });
      }
    } catch {
      toast({ title: "Error", description: "Connection error", variant: "destructive" });
    }
    setSettingPassword(false);
  };

  const fetchRofrs = async () => {
    try {
      const res = await fetch(`${BASE}api/rofr-opportunities`);
      if (res.ok) setRofrs(await res.json());
    } catch {}
  };

  const handleCreateRofr = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    try {
      const res = await fetch(`${BASE}api/rofr-opportunities`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          companyId: parseInt(form.get("companyId") as string, 10),
          roundName: form.get("roundName") as string,
          roundSize: (form.get("roundSize") as string) || null,
          valuation: (form.get("valuation") as string) || null,
          leadInvestor: (form.get("leadInvestor") as string) || null,
          terms: (form.get("terms") as string) || null,
          deadline: (form.get("deadline") as string) || null,
          notes: (form.get("notes") as string) || null,
        }),
      });
      if (res.ok) {
        toast({ title: "ROFR opportunity created" });
        fetchRofrs();
      }
    } catch {
      toast({ title: "Error creating ROFR", variant: "destructive" });
    }
  };

  const handleCloseRofr = async (id: number) => {
    try {
      const res = await fetch(`${BASE}api/rofr-opportunities/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "closed" }),
      });
      if (res.ok) {
        toast({ title: "ROFR closed" });
        fetchRofrs();
      }
    } catch {}
  };

  const fetchInvestorDocs = async (investorId: number) => {
    try {
      const res = await fetch(`${BASE}api/investor-documents/${investorId}`);
      if (res.ok) setInvestorDocs(await res.json());
    } catch {}
  };

  const handleUploadDoc = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!docInvestorId) return;
    const form = new FormData(e.currentTarget);
    try {
      const res = await fetch(`${BASE}api/investor-documents`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          investorId: docInvestorId,
          companyId: form.get("companyId") ? parseInt(form.get("companyId") as string, 10) : null,
          title: form.get("title") as string,
          type: form.get("type") as string,
          url: form.get("url") as string,
          notes: (form.get("notes") as string) || null,
        }),
      });
      if (res.ok) {
        toast({ title: "Document added" });
        fetchInvestorDocs(docInvestorId);
        (e.target as HTMLFormElement).reset();
      }
    } catch {
      toast({ title: "Error", variant: "destructive" });
    }
  };

  const handleDeleteDoc = async (docId: number) => {
    if (!docInvestorId) return;
    try {
      await fetch(`${BASE}api/investor-documents/${docId}`, { method: "DELETE" });
      fetchInvestorDocs(docInvestorId);
      toast({ title: "Document removed" });
    } catch {}
  };

  const portalUrl = typeof window !== "undefined"
    ? `${window.location.origin}${BASE}investor/login`
    : "";

  return (
    <div className="space-y-6" data-testid="page-investors">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-serif font-bold tracking-tight">Investors</h1>
            <InfoTip content="Manage which investors can see which companies. Each investor gets a permissioned Intelligence Room with only the companies you've granted them access to. You control whether they see 'standard' (basic overview) or 'detailed' (full KPIs + documents) information." />
          </div>
          <p className="text-sm text-muted-foreground mt-1">Manage investor relationships and access permissions</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => { setRofrDialogOpen(true); fetchRofrs(); }}>
            <Bell className="w-4 h-4 mr-2" />ROFR Manager
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              navigator.clipboard.writeText(portalUrl);
              toast({ title: "Portal URL copied" });
            }}
          >
            <Copy className="w-4 h-4 mr-2" />Copy Portal Link
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="button-add-investor"><Plus className="w-4 h-4 mr-2" />Add Investor</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-serif">Add Investor</DialogTitle></DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Name</Label><Input name="name" required data-testid="input-investor-name" /></div>
                  <div className="space-y-2"><Label>Email</Label><Input name="email" type="email" required data-testid="input-investor-email" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <select name="type" className="w-full h-9 rounded-md border bg-background px-3 text-sm" required data-testid="select-investor-type">
                      <option value="angel">Angel</option>
                      <option value="vc">VC</option>
                      <option value="family_office">Family Office</option>
                      <option value="syndicate">Syndicate</option>
                    </select>
                  </div>
                  <div className="space-y-2"><Label>Firm</Label><Input name="firm" data-testid="input-investor-firm" /></div>
                </div>
                <div className="space-y-2"><Label>Notes</Label><Textarea name="notes" data-testid="input-investor-notes" /></div>
                <Button type="submit" className="w-full" disabled={createMutation.isPending} data-testid="button-submit-investor">
                  {createMutation.isPending ? "Creating..." : "Add Investor"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Dialog open={accessDialogOpen} onOpenChange={open => {
        if (savingCohorts || grantingBatch) return;
        setAccessDialogOpen(open);
        if (!open) setCohortDraftReady(false);
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif">
              Manage Access — {investors?.find(i => i.id === selectedInvestorId)?.name}
            </DialogTitle>
            <DialogDescription>Manage cohort memberships and company investment allocations independently.</DialogDescription>
          </DialogHeader>
          <section className="space-y-3" aria-label="Investor cohort memberships">
            <div>
              <h3 className="text-sm font-semibold">1. Investor cohort memberships</h3>
              <p className="text-xs text-muted-foreground">Select every cohort this investor belongs to. Adding a cohort keeps their existing memberships. Research Pipeline PIM access follows the cohort's approved release policy; existing individual assignments remain separate.</p>
            </div>
            {membershipQuery.isError ? (
              <div role="alert" className="rounded-md border border-destructive/40 p-3 text-sm">
                Could not load cohort memberships: {String(membershipQuery.error?.message ?? "Unknown error")}.
                <Button variant="outline" size="sm" className="ml-2" onClick={() => { void membershipQuery.refetch(); }}>Retry</Button>
              </div>
            ) : membershipQuery.isLoading || (membershipQuery.isSuccess && !cohortDraftReady) ? (
              <p className="text-sm text-muted-foreground">Loading cohort memberships…</p>
            ) : membershipQuery.data?.length === 0 ? (
              <p className="text-sm text-muted-foreground">No cohorts exist yet. Create a cohort before assigning investors.</p>
            ) : (
              <div className="max-h-40 overflow-y-auto space-y-1">
                {membershipQuery.data?.map(cohort => (
                  <label key={cohort.id} className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={selectedCohorts.includes(cohort.id)} disabled={savingCohorts} onChange={event =>
                      setSelectedCohorts(current => event.target.checked ? [...current, cohort.id] : current.filter(id => id !== cohort.id))
                    } />
                    {cohort.name}
                  </label>
                ))}
              </div>
            )}
            <Button size="sm" onClick={handleSaveCohorts} disabled={savingCohorts || membershipQuery.isFetching || !cohortDraftReady || !membershipQuery.isSuccess || !membershipQuery.data?.length} data-testid="button-save-cohort-memberships">
              {savingCohorts ? "Saving memberships…" : "Save cohort memberships"}
            </Button>
          </section>
          <div className="border-t pt-3 space-y-1">
            <h3 className="text-sm font-semibold">2. Company access &amp; investment allocation</h3>
            <p className="text-xs text-muted-foreground">Company permissions and invested amounts are managed separately from cohort memberships. Saving memberships does not change these allocations.</p>
          </div>
          {cohortList.isError ? (
            <p role="alert" className="text-sm text-destructive">Could not load cohorts: {String(cohortList.error?.message ?? "Unknown error")}. Retry loading cohorts above.</p>
          ) : cohortSlug && !cohortLoading && !cohortId ? (
            <p role="alert" className="text-sm text-destructive">Cohort “{cohortSlug}” was not found. Company allocations cannot be edited here.</p>
          ) : companyQuery.isError || accessQuery.isError ? (
            <div role="alert" className="text-sm text-destructive">
              Could not load company allocation data: {String((companyQuery.error ?? accessQuery.error)?.message ?? "Unknown error")}.
              <Button size="sm" variant="outline" onClick={() => { void companyQuery.refetch(); void accessQuery.refetch(); }}>Retry</Button>
            </div>
          ) : companyQuery.isLoading || accessQuery.isLoading || cohortLoading ? (
            <p className="text-sm text-muted-foreground">Loading company allocations…</p>
          ) : companies?.length === 0 ? (
            <p className="text-sm text-muted-foreground">No companies are assigned to this cohort. Existing investments and totals remain unchanged.</p>
          ) : (
          <>
          <div className="flex items-center gap-2 pb-2 border-b">
            <Button size="sm" variant="outline" className="text-xs gap-1" onClick={() => grantAll("full")}>
              <Check className="w-3 h-3" />Grant All (Full)
            </Button>
            <Button size="sm" variant="outline" className="text-xs gap-1" onClick={() => grantAll("summary")}>
              <Check className="w-3 h-3" />Grant All (Summary)
            </Button>
            <Button size="sm" variant="ghost" className="text-xs gap-1 text-muted-foreground" onClick={clearAll}>
              <X className="w-3 h-3" />Clear All
            </Button>
          </div>
          {/* Bulk-distribute panel — type a total, click Distribute equally,
              and the amount auto-fills onto every selected (checked) company.
              Saved alongside the access grants when "Save Access" is clicked. */}
          <div className="rounded-lg border border-orange-200 bg-orange-50/50 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium">Distribute total across selected companies (optional)</Label>
              <span className="text-[10px] font-mono text-muted-foreground">{selectedCount} selected</span>
            </div>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <IndianRupee className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="number"
                  value={bulkTotalStr}
                  onChange={e => setBulkTotalStr(e.target.value)}
                  placeholder="e.g. 5000000"
                  className="pl-7 h-8 text-sm"
                  data-testid="input-bulk-total-amount"
                />
              </div>
              <Button
                onClick={distributeEqually}
                size="sm"
                disabled={!(Number(bulkTotalStr) > 0) || selectedCount === 0}
                data-testid="button-distribute-equally"
              >
                <Check className="w-3 h-3 mr-1" /> Distribute equally
              </Button>
            </div>
            {selectedCount > 0 && Number(bulkTotalStr) > 0 ? (
              <p className="text-[11px] text-muted-foreground">
                Splits to <span className="font-medium text-foreground">{fmtINRShort(Math.floor(Number(bulkTotalStr) / selectedCount))}</span> per company
                {Number(bulkTotalStr) % selectedCount !== 0 ? ` (remainder ₹${Number(bulkTotalStr) % selectedCount} → first row)` : ""}.
              </p>
            ) : (
              <p className="text-[11px] text-muted-foreground">Pick the companies below first, then enter a total to spread across them.</p>
            )}
          </div>
          <div className="space-y-1 max-h-[40vh] overflow-y-auto py-1">
            {companies?.map(c => {
              const level = accessSelections[c.id];
              const amount = amountSelections[c.id];
              return (
                <div
                  key={c.id}
                  className={`flex items-center justify-between rounded-lg px-3 py-2.5 cursor-pointer transition-colors ${
                    level ? "bg-primary/5 border border-primary/20" : "bg-muted/30 border border-transparent hover:bg-muted/60"
                  }`}
                  onClick={() => toggleCompanyAccess(c.id)}
                  data-testid={`access-row-${c.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      level ? "bg-primary border-primary" : "border-muted-foreground/30"
                    }`}>
                      {level && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <span className={`text-sm ${level ? "font-medium" : "text-muted-foreground"}`}>{c.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {level && amount && amount > 0 && (
                      <span className="text-[10px] font-mono font-medium text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded" data-testid={`amount-${c.id}`}>
                        {fmtINRShort(amount)}
                      </span>
                    )}
                    {level && (
                      <button
                        onClick={(e) => { e.stopPropagation(); cycleAccessLevel(c.id); }}
                        className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full transition-colors ${
                          level === "full"
                            ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                            : "bg-amber-100 text-amber-700 hover:bg-amber-200"
                        }`}
                        data-testid={`access-level-${c.id}`}
                      >
                        {level}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="pt-2 border-t flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              {selectedCount} of {companies?.length ?? 0} companies selected
            </p>
            <Button onClick={handleSaveAccess} disabled={grantingBatch || !companyQuery.isSuccess || !accessQuery.isSuccess} data-testid="button-save-access">
              {grantingBatch ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : "Save Access"}
            </Button>
          </div>
          </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={passwordDialogOpen} onOpenChange={(open) => { setPasswordDialogOpen(open); if (!open) setNewPassword(""); }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-serif">Set Portal Password</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Set or reset the Favcy Select investor portal password for this investor.</p>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input type="text" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min 6 characters" />
            </div>
            <Button onClick={handleSetPassword} disabled={settingPassword || newPassword.length < 6} className="w-full">
              {settingPassword ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Setting...</> : <><Key className="w-4 h-4 mr-2" />Set Password</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={rofrDialogOpen} onOpenChange={setRofrDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif">ROFR Opportunity Manager</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateRofr} className="space-y-4 border rounded-lg p-4">
            <p className="text-sm font-medium">Create New ROFR Opportunity</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Company</Label>
                <select name="companyId" className="w-full h-9 rounded-md border bg-background px-3 text-sm" required>
                  <option value="">Select</option>
                  {companies?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="space-y-1"><Label className="text-xs">Round Name</Label><Input name="roundName" placeholder="Series A" required /></div>
              <div className="space-y-1"><Label className="text-xs">Round Size</Label><Input name="roundSize" placeholder="₹5Cr" /></div>
              <div className="space-y-1"><Label className="text-xs">Valuation</Label><Input name="valuation" placeholder="₹50Cr" /></div>
              <div className="space-y-1"><Label className="text-xs">Lead Investor</Label><Input name="leadInvestor" /></div>
              <div className="space-y-1"><Label className="text-xs">Deadline</Label><Input name="deadline" type="date" /></div>
            </div>
            <div className="space-y-1"><Label className="text-xs">Terms</Label><Textarea name="terms" rows={2} /></div>
            <div className="space-y-1"><Label className="text-xs">Notes</Label><Textarea name="notes" rows={2} /></div>
            <Button type="submit" size="sm"><Plus className="w-3 h-3 mr-1" />Create</Button>
          </form>

          {rofrs.length > 0 && (
            <div className="space-y-3 mt-4">
              <p className="text-sm font-medium">Existing Opportunities</p>
              {rofrs.map((rofr: any) => (
                <div key={rofr.id} className={`border rounded-lg p-4 ${rofr.status === "active" ? "" : "opacity-50"}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-medium text-sm">{rofr.companyName} — {rofr.roundName}</p>
                      <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
                        {rofr.roundSize && <span>Size: {rofr.roundSize}</span>}
                        {rofr.valuation && <span>Val: {rofr.valuation}</span>}
                        <Badge variant={rofr.status === "active" ? "default" : "secondary"} className="text-[10px]">{rofr.status}</Badge>
                      </div>
                    </div>
                    {rofr.status === "active" && (
                      <Button variant="outline" size="sm" onClick={() => handleCloseRofr(rofr.id)}>Close</Button>
                    )}
                  </div>
                  {rofr.responses && rofr.responses.length > 0 && (
                    <div className="mt-2 pt-2 border-t space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Responses ({rofr.responses.length})</p>
                      {rofr.responses.map((r: any) => (
                        <div key={r.id} className="flex items-center justify-between text-xs">
                          <span>{r.investorName}</span>
                          <Badge variant={r.response === "interested" ? "default" : "secondary"} className="text-[10px]">{r.response}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={docDialogOpen} onOpenChange={setDocDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif">Investor Documents</DialogTitle></DialogHeader>
          <form onSubmit={handleUploadDoc} className="space-y-3 border rounded-lg p-4">
            <p className="text-sm font-medium">Add Document</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">Title</Label><Input name="title" required /></div>
              <div className="space-y-1">
                <Label className="text-xs">Type</Label>
                <select name="type" className="w-full h-9 rounded-md border bg-background px-3 text-sm">
                  <option value="agreement">Agreement</option>
                  <option value="share_transfer">Share Transfer</option>
                  <option value="rofr_agreement">ROFR Agreement</option>
                  <option value="tax">Tax Document</option>
                  <option value="report">Report</option>
                </select>
              </div>
            </div>
            <div className="space-y-1"><Label className="text-xs">Document URL</Label><Input name="url" type="url" required /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Company (optional)</Label>
                <select name="companyId" className="w-full h-9 rounded-md border bg-background px-3 text-sm">
                  <option value="">None</option>
                  {companies?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="space-y-1"><Label className="text-xs">Notes</Label><Input name="notes" /></div>
            </div>
            <Button type="submit" size="sm"><Plus className="w-3 h-3 mr-1" />Add</Button>
          </form>

          {investorDocs.length > 0 && (
            <div className="space-y-2 mt-4">
              <p className="text-sm font-medium">Uploaded Documents</p>
              {investorDocs.map((doc: any) => (
                <div key={doc.id} className="flex items-center justify-between border rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">{doc.title}</p>
                      <p className="text-xs text-muted-foreground">{doc.type}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <a href={doc.url} target="_blank" rel="noopener noreferrer">
                      <Button variant="ghost" size="sm"><ExternalLink className="w-3 h-3" /></Button>
                    </a>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteDoc(doc.id)}>
                      <Trash2 className="w-3 h-3 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {isLoading ? (
        <div className="space-y-4">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-32" />)}</div>
      ) : investors && investors.length > 0 ? (
        <div className="space-y-4">
          {investors.map((investor) => {
            const investorAccessItems = (allAccess ?? []).filter(a => a.investorId === investor.id);
            return (
              <Card key={investor.id} data-testid={`card-investor-${investor.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <UserCircle className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-medium">{investor.name}</h3>
                        <p className="text-xs text-muted-foreground">{investor.email}{investor.firm ? ` | ${investor.firm}` : ""}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      <Badge className={`text-xs ${typeBadgeStyles[investor.type] || ""}`}>{investor.type.replace("_", " ")}</Badge>
                      {investor.firstLoggedInAt ? (
                        <Badge variant="outline" className="text-xs gap-1 border-emerald-300 text-emerald-700 bg-emerald-50" data-testid={`badge-logged-in-${investor.id}`}>
                          <MailCheck className="w-3 h-3" />Logged in
                        </Badge>
                      ) : (
                        <Button
                          variant={investor.onboardingEmailSentAt ? "outline" : "default"}
                          size="sm"
                          onClick={() => handleSendOnboarding(investor)}
                          disabled={!!onboardingInFlight[investor.id]}
                          data-testid={`button-send-onboarding-${investor.id}`}
                          title={
                            investor.onboardingEmailSentAt
                              ? `Last sent ${formatRelative(investor.onboardingEmailSentAt)} — click to re-send`
                              : "Send the password-setup email so this investor can log in"
                          }
                        >
                          {onboardingInFlight[investor.id] ? (
                            <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Sending…</>
                          ) : investor.onboardingEmailSentAt ? (
                            <><Mail className="w-3 h-3 mr-1" />Resend onboarding · {formatRelative(investor.onboardingEmailSentAt)}</>
                          ) : (
                            <><Mail className="w-3 h-3 mr-1" />Send onboarding</>
                          )}
                        </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={() => { setPasswordInvestorId(investor.id); setPasswordDialogOpen(true); }}>
                        <Key className="w-3 h-3 mr-1" />Portal
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => { setDocInvestorId(investor.id); fetchInvestorDocs(investor.id); setDocDialogOpen(true); }}>
                        <FileText className="w-3 h-3 mr-1" />Docs
                      </Button>
                      <ExportPortfolioInlineButton investorId={investor.id} investorName={investor.name} holdingCount={investorAccessItems.length} />
                      <Button variant="outline" size="sm" onClick={() => { setSelectedInvestorId(investor.id); setAccessDialogOpen(true); }} data-testid={`button-grant-access-${investor.id}`}>
                        <Shield className="w-3 h-3 mr-1" />Grant Access
                      </Button>
                    </div>
                  </div>
                  {investor.notes && <p className="text-sm text-muted-foreground mt-3">{investor.notes}</p>}
                  {investorAccessItems.length > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Company Access</p>
                      <div className="flex flex-wrap gap-2">
                        {investorAccessItems.map((access) => {
                          const amt = access.amountInvested ? Number(access.amountInvested) : 0;
                          return (
                            <Badge key={access.id} variant="outline" className="text-xs gap-1" data-testid={`badge-access-${access.id}`}>
                              {companyMap.get(access.companyId) ?? `#${access.companyId}`} ({access.accessLevel})
                              {amt > 0 && <span className="text-orange-700 font-mono ml-1">{fmtINRShort(amt)}</span>}
                              <button onClick={() => handleRevoke(access.id)} className="ml-1 hover:text-destructive" data-testid={`button-revoke-${access.id}`}>
                                <X className="w-3 h-3" />
                              </button>
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <UserCircle className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="font-medium">No investors yet</p>
            <p className="text-sm text-muted-foreground mt-1">Add investors to manage access permissions</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Per-investor portfolio CSV download, shown inline next to Grant Access.
// Same gating as the admin-list version: disabled (with tooltip) when the
// investor has zero holdings, so nobody clicks it expecting a file and gets
// an empty one. Uses authHeaders() because the endpoint requires the admin
// token — a plain <a href> won't carry it.
function ExportPortfolioInlineButton({ investorId, investorName, holdingCount }: { investorId: number; investorName: string; holdingCount: number }) {
  const [busy, setBusy] = useState(false);
  const empty = holdingCount === 0;
  const onClick = async () => {
    if (empty || busy) return;
    setBusy(true);
    try {
      const r = await fetch(`${BASE}api/admin/investors/${investorId}/portfolio.csv`, { headers: authHeaders() });
      if (!r.ok) { alert(`Export failed (${r.status}).`); setBusy(false); return; }
      const blob = await r.blob();
      const disp = r.headers.get("Content-Disposition") || "";
      const m = disp.match(/filename="([^"]+)"/);
      const filename = m?.[1] || `portfolio_${investorName.replace(/[^a-z0-9]+/gi, "_")}.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { alert("Network error while exporting"); }
    setBusy(false);
  };
  return (
    <Button
      variant="outline" size="sm" onClick={onClick} disabled={empty || busy}
      data-testid={`button-export-portfolio-${investorId}`}
      title={empty ? "No holdings to export yet" : `Download ${investorName}'s portfolio as CSV`}
    >
      {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FileSpreadsheet className="w-3 h-3 mr-1" />}
      {busy ? "Exporting…" : "Export"}
    </Button>
  );
}
